/**
 * Iter 67en+++++ — Small "trust signals" pill row for the hero section.
 *
 * Five short reassurance pills (icon + 1–2 words) that the renter
 * scans in under a second before they engage with the search form.
 * Replaces the prose-style "No credit card, low deposit, no hidden
 * costs" sentence with a more scannable, vertically-compact format.
 *
 * Two visual variants:
 *  • `light` (default)  — neutral pill on white background, used on
 *                          mobile/tablet (which doesn't have the dark
 *                          hero photo overlay).
 *  • `dark`             — semi-transparent white pill on the dark
 *                          gradient over the hero photo (desktop).
 *
 * Used in two places: `Home.jsx` mobile block + desktop overlay. Kept
 * as a tiny reusable component so the icon set and copy stay
 * synchronised between the two.
 */
import { CreditCard, Wallet, CalendarX, Eye, Zap, Gauge } from "lucide-react";
import { useTranslation } from "react-i18next";

const ITEMS = [
  { key: "no_card",        i18n: "home.hero.pill.no_card",        fallback: "No credit card",       Icon: CreditCard },
  { key: "low_deposit",    i18n: "home.hero.pill.low_deposit",    fallback: "Low or no deposit",    Icon: Wallet },
  { key: "free_cancel",    i18n: "home.hero.pill.free_cancel",    fallback: "Free cancellation",    Icon: CalendarX },
  { key: "unlimited_km",   i18n: "home.hero.pill.unlimited_km",   fallback: "Unlimited kilometers", Icon: Gauge },
  { key: "transparent",    i18n: "home.hero.pill.transparent",    fallback: "Transparent prices",   Icon: Eye },
  { key: "book_seconds",   i18n: "home.hero.pill.book_seconds",   fallback: "Book in seconds",      Icon: Zap },
];

export default function HeroTrustPills({ variant = "light" }) {
  const { t } = useTranslation();
  // Two complete class strings (vs. dynamic concat) so Tailwind JIT
  // catches both variants during build.
  const pillCls = variant === "dark"
    ? "bg-white/15 backdrop-blur-md text-white border-white/25 drop-shadow"
    : "bg-white text-neutral-800 border-neutral-200 shadow-sm";
  const iconCls = variant === "dark" ? "text-amber-300" : "text-amber-600";
  // On the desktop overlay (`dark`) variant we force a single horizontal
  // row — all 5 pills MUST fit beside each other on lg+ so the hero
  // reads as one tight reassurance band. On mobile (`light`) we keep
  // flex-wrap because the narrow viewport guarantees a 2-row layout.
  const containerCls = variant === "dark"
    ? "mt-5 flex flex-wrap xl:flex-nowrap xl:max-w-4xl gap-1.5"
    : "mt-5 flex flex-wrap gap-2";

  return (
    <ul
      className={containerCls}
      data-testid="hero-trust-pills"
      aria-label="Booking guarantees"
    >
      {ITEMS.map(({ key, i18n, fallback, Icon }) => (
        <li
          key={key}
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-xs font-semibold tracking-tight whitespace-nowrap ${pillCls}`}
          data-testid={`hero-trust-pill-${key}`}
        >
          <Icon className={`h-3.5 w-3.5 shrink-0 ${iconCls}`} strokeWidth={2.25} />
          <span>{t(i18n, fallback)}</span>
        </li>
      ))}
    </ul>
  );
}
